import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BiEditAlt,
  BiChevronsRight,
  BiChevronsLeft,
  BiCog,
  BiShoppingBag,
} from "react-icons/bi";
import Spinner from "../Spinner";
import { useFetch } from "../../../Hooks/useFetch";

const PaginatedTableWithFetchGroups = ({
  api,
  pageSize,
  columns,
  fields,
  onEdit,
  onDelete,
  onConfig,
  onCarShopping,
  tag,
  shouldShowActions = () => true,
}) => {
  const { t } = useTranslation();
  const [initPageIndex, setInitPageIndex] = useState(0);
  const [endPageIndex, setEndPageIndex] = useState(pageSize);
  const [paginateIndex, setPaginateIndex] = useState(1);
  const [filteredIds, setFilteredIds] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [columnFilters, setColumnFilters] = useState({});

  const { data, error, isDisabled } = useFetch(api, tag);

  // 📑 Paginação
  const amountPages = () => {
    const pages = Math.ceil(
      (filteredIds.length > 0 ? filteredIds.length : data.length) / pageSize
    );
    return pages;
  };

  const onPaginate = (index) => {
    if (index > 0 && index <= amountPages()) {
      setPaginateIndex(index);
      setInitPageIndex((index - 1) * pageSize);
      setEndPageIndex(index * pageSize);
    }
  };

  // 🔍 Filtro global (barra de busca)
  const onSearch = (filter) => {
    const searchTerm = filter.trim().toUpperCase();

    if (!searchTerm) {
      const allIds = data.map((row) => row.vehicleGroupName);
      setFilteredIds(allIds);
      return;
    }

    const matchingIds = data
      .filter((row) =>
        ["vehicleGroupName", "standardFlag"].some((field) => {
          const value = row[field];
          return value && value.toString().toUpperCase().includes(searchTerm);
        })
      )
      .map((row) => row.vehicleGroupName);

    setFilteredIds(matchingIds);
  };

  // 🔽 Ordenação
  const handleSort = (columnKey) => {
    let direction = "asc";
    if (sortConfig.key === columnKey && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: columnKey, direction });
  };

  // 🔍 Filtro por coluna
  const onColumnFilter = (field, value) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value.toUpperCase() }));
  };

  // ⚙️ Renderizar botões de ação
  const buttons = (item) => (
    <div className="row">
      <div className="col-4">
        <BiEditAlt className="icon-button" onClick={() => onEdit(item)} />
      </div>

      <div className="col-4">
        <BiCog className="icon-button" onClick={() => onConfig(item)} />
      </div>
    </div>
  );

  // ⚠️ Tratamento de erros e carregamento
  if (error)
    return (
      <h6 style={{ color: "red", paddingLeft: "20px" }}>
        {t("TABLE.LOAD_DATA_ERROR")}
      </h6>
    );
  if (isDisabled)
    return (
      <div className="alert alert-warning" style={{ margin: "20px" }}>
        {t("TABLE.SELECT_OPTION")}
      </div>
    );
  if (!data) return <Spinner />;

  // 🧩 Aplicar filtros e ordenação
  let filtereds =
    filteredIds.length > 0
      ? data.filter((d) => filteredIds.includes(d.vehicleGroupName))
      : [...data];

  // Filtrar por coluna
  filtereds = filtereds.filter((row) =>
    Object.entries(columnFilters).every(([key, val]) => {
      if (!val) return true;
      const cellValue = row[key]?.toString().toUpperCase() || "";
      return cellValue.includes(val);
    })
  );

  // Ordenar
  if (sortConfig.key) {
    filtereds.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }

  return (
    <>
      {/* 🔍 Busca global */}
      <div className="d-flex align-items-end flex-column bd-highlight mb-3">
        <div className="p-2 bd-highlight">
          <input
            id="table-search"
            className="form-control me-2"
            type="search"
            placeholder={t("TABLE.GLOBAL_SEARCH")}
            aria-label="Search"
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
      </div>

      {/* 🧾 Tabela */}
      <div className="table-responsive text-nowrap">
        <table className="table" id="paginated-table">
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th
                  key={index}
                  style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  onClick={() => handleSort(fields[index])}
                >
                  {col}
                  {sortConfig.key === fields[index] && (
                    <span style={{ marginLeft: 5 }}>
                      {sortConfig.direction === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
              ))}
              <th>{t("TABLE.ACTIONS")}</th>
            </tr>

            {/* 🔍 Linha de filtros por coluna */}
            <tr>
              {fields.map((field, index) => (
                <th key={index}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("TABLE.FILTER")}
                    onChange={(e) => onColumnFilter(field, e.target.value)}
                  />
                </th>
              ))}
              <th></th>
            </tr>
          </thead>

          <tbody className="table-border-bottom-0">
            {filtereds.slice(initPageIndex, endPageIndex).map((item, index) => (
              <tr key={index}>
                {fields.map((field, i) => (
                  <td key={i}>
                    {item[field] === "N" ? (
                      <span>{t("CLIENT_DETAILS.NO")}</span>
                    ) : item[field] === "S" ? (
                      <span style={{ color: "green", fontWeight: "700" }}>
                        {t("CLIENT_DETAILS.YES")}
                      </span>
                    ) : (
                      item[field]
                    )}
                  </td>
                ))}
                <td className="action-table-col" style={{ minWidth: "100px" }}>
                  {shouldShowActions(item) && buttons(item)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 🔄 Paginação */}
        <nav aria-label="Page navigation">
          <ul className="pagination">
            <li className="page-item">
              <button
                className="page-link"
                onClick={() => onPaginate(paginateIndex - 1)}
              >
                <BiChevronsLeft />
              </button>
            </li>
            {Array.from({ length: amountPages() }, (_, i) => i + 1).map(
              (item) => (
                <li
                  key={item}
                  className={`page-item ${
                    paginateIndex === item ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => onPaginate(item)}
                  >
                    {item}
                  </button>
                </li>
              )
            )}
            <li className="page-item">
              <button
                className="page-link"
                onClick={() => onPaginate(paginateIndex + 1)}
              >
                <BiChevronsRight />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};

export default PaginatedTableWithFetchGroups;